/**
 * 홈 장면 다섯이 펴는 **예시 데이터** — 실제 서비스 부품(카드 격자 · 허브 카드 · 크리에이터 줄 · 추적 카드 · 시딩 카드)에 그대로 넣는 값이다.
 *
 * 🔑 규모는 실제 수집분에서 왔다(반올림 허용) — 숫자를 지어내지 않는다.
 * 🔑 그림은 실제 게시물·광고·상품 이미지를 웹용으로 줄여 `public/assets/home/scenes/` 에 둔 것이다(서명 주소는 만료되어 걸지 않는다).
 * ⛔ **계정 정보는 실제 값을 쓰지 않는다** — 아이디·이름·프로필 사진은 가상 핸들과 머리글자 얼굴이고, 캡션은 해시태그만 남긴다.
 *   브랜드 이름(아누아·토리든 …)과 브랜드 광고 페이지는 실제 이름 그대로다.
 * ⚠️ 서버를 부르지 않는다 — 공개 홈은 로그인 없이 그려져야 한다.
 */

import type { MomentAnalyticsPivotResponse } from 'src/types/moments/moment-ops';
import type { MediaCard } from 'src/sections/search-all/utils/search-all-media-cards';
import type { AlertPreviewSample } from 'src/components/cmui/alert/alert-rule-preview';

import { CONFIG } from 'src/global-config';

import { dateColumns, singleSeries, sampleCardBuilders } from '../utils/public-sample-cards';

// ----------------------------------------------------------------------

const SCENE_DIR = `${CONFIG.assetsDir}/assets/home/scenes`;

/** 장면 그림 한 장의 주소 */
export function sceneAsset(file: string): string {
  return `${SCENE_DIR}/${file}`;
}

const { organic, ad, product } = sampleCardBuilders('home', sceneAsset);

// ---- 1. 컨텐츠 허브 ---------------------------------------------------------

/** 컨텐츠 허브 장면의 검색어 */
export const CONTENTS_QUERY = 'rhode';

/**
 * 「rhode」로 찾은 격자 — 해외 크리에이터 글 넷 · 집행 중인 rhode Meta 광고 셋 · 판매몰(화해) 상품 셋.
 * 🔑 카드는 전부 정적 섬네일이다 — 영상을 싣지 않는다(재생 UI 가 카드 위에 그려지지 않게).
 * ⚠️ **차례가 배치다** — 넓은 화면은 다섯 장씩 두 줄이고, 줄마다 넓은 그림(상품 1:1 · 광고 4:5)이 섞여야 줄이 폭을 꽉 채운다
 *   (세로 9:16 만 모이면 줄 높이가 천장에 걸려 오른쪽이 빈다). 핸들은 좁은 카드에서도 다 읽히게 짧게 둔다.
 * ⛔ 셀럽 얼굴·실명이 보이는 글·광고·상품명은 쓰지 않는다(창업자 포함 — 공개 지면의 초상권).
 * ⚠️ 화해 상품은 가격을 주지 않아 가격 줄이 비고, 리뷰 수가 적다 — 받은 값 그대로다.
 */
export const CONTENTS_CARDS: MediaCard[] = [
  organic({ id: 'rhode-swatch', name: 'lilo', channel: 'tiktok', date: '2026-09-24', views: 1070039, likes: 122041, comments: 199, title: '#rhodeblushes #monetblush' }),
  ad({ id: 'rhode-makeup', name: 'rhode', start: '2026.09.25', days: 7, cta: '지금 구매하기', title: 'more than makeup 🎀 hybrid lip and cheek essentials' }),
  product({ id: 'rhode-tint', brand: 'rhode', mall: '화해', rank: 2, price: 0, rating: 4.3, reviews: 3, title: '(해외) 펩타이드 립 틴트 [토스트]' }),
  organic({ id: 'short-rhode-blush', name: 'juno', channel: 'tiktok', date: '2026-10-01', views: 360233, likes: 32392, comments: 76, title: '#rhodetanline #rhodeblush', thumb: 'short-rhode-blush.webp' }),
  organic({ id: 'rhode-lip', name: 'tess', channel: 'tiktok', date: '2026-09-19', views: 2914208, likes: 86680, comments: 151, title: '#rhodeskin #makeupfavorites' }),
  organic({ id: 'rhode-tints', name: 'rei', channel: 'tiktok', date: '2026-10-01', views: 78129, likes: 3567, comments: 5, title: '#rhode #lipcombo' }),
  ad({ id: 'rhode-milk', name: 'rhode', start: '2026.09.02', days: 30, cta: '지금 구매하기', title: 'soft shimmer 💫 highlight milk melts into skin' }),
  product({ id: 'rhode-blush', brand: 'rhode', mall: '화해', rank: 3, price: 0, rating: 5, reviews: 1, title: '(해외) 포켓 블러쉬 [피기]' }),
  ad({ id: 'rhode-tints', name: 'rhode', start: '2026.09.25', days: 7, cta: '가입하기', title: 'the peptide lip tints 🎀' }),
  product({ id: 'rhode-milk', brand: 'rhode', mall: '화해', rank: 1, price: 0, rating: 4, reviews: 1, title: '(해외) 글레이징 밀크' }),
];

// ---- 2. 브랜드 허브 ---------------------------------------------------------

/** 브랜드 허브 장면의 브랜드 */
export const BRAND_NAME = '토리든';

/** 머리 카드의 요약 숫자 — 최근 7일과 그 앞 7일 · 13주 합 · 집행 중 광고 · 랭킹에 오른 상품 */
export const BRAND_HEAD = {
  recent7d: 3290,
  prev7d: 2989,
  total: 51357,
  topChannel: 'X',
  adTotal: 876,
  productTotal: 228,
};

/** 13주 언급 추이 — 7일 묶음 · 브랜드 허브 추이가 받는 서버 답과 같은 모양(줄 하나 · 열이 날짜) */
export const BRAND_TREND: MomentAnalyticsPivotResponse = dateColumns([['언급', [
  ['2026-07-04', 3840],
  ['2026-07-11', 3730],
  ['2026-07-18', 3071],
  ['2026-07-25', 3196],
  ['2026-08-01', 3781],
  ['2026-08-08', 3952],
  ['2026-08-15', 4984],
  ['2026-08-22', 5462],
  ['2026-08-29', 5538],
  ['2026-09-05', 4245],
  ['2026-09-12', 3279],
  ['2026-09-19', 2989],
  ['2026-09-26', 3290],
]]]);

/** 같은 글에서 함께 언급된 브랜드 — 같은 13주 · 글 수 */
export const BRAND_CO_MENTIONS = [
  { name: '아누아', count: 4386 },
  { name: '메디큐브', count: 2667 },
  { name: 'SKIN1004', count: 2330 },
  { name: '메디힐', count: 1867 },
  { name: '라운드랩', count: 1684 },
  { name: '조선미녀', count: 1657 },
  { name: '코스알엑스', count: 1552 },
  { name: '넘버즈인', count: 1403 },
];

// ---- 3. 크리에이터 허브 -----------------------------------------------------

/**
 * 크리에이터 목록 여섯 줄 — 실제 인스타그램 계정(미국)의 30일 지표에 가상 핸들을 붙였다.
 * 실화면 줄 파생(`toCreatorRowView`)이 읽는 발굴 문서 모양이다. `previews` 는 그 계정의 최근 글 그림이다.
 */
export const CREATOR_DOCS: { doc: Record<string, unknown>; previews: Record<string, unknown>[] }[] = [
  creator('A', 'luxe.lather', 1484987, 755638, 0.7, 7, 3),
  creator('B', 'glam.studio.co', 2286634, 1134264, 0.8, 5, 3),
  creator('C', 'weekend.edit', 1365886, 314438, 3.7, 7, 3),
  creator('D', 'quiet.skin', 15469, 167724, 27.3, 4, 2),
  creator('E', 'dewdrop.notes', 29218, 698726, 26.4, 21, 3),
  creator('F', 'undereye.club', 24039, 391610, 23.9, 5, 3),
];

// ---- 4. 추적 ---------------------------------------------------------------

/** 추적 등록 창의 「최근 1년」 카드 값 — 다 지난 12달 */
export const TRACK_YEAR_MONTHS = [
  { month: '2025-10', count: 37520 },
  { month: '2025-11', count: 49303 },
  { month: '2025-12', count: 54235 },
  { month: '2026-01', count: 58117 },
  { month: '2026-02', count: 63585 },
  { month: '2026-03', count: 74010 },
  { month: '2026-04', count: 73964 },
  { month: '2026-05', count: 76712 },
  { month: '2026-06', count: 88086 },
  { month: '2026-07', count: 85445 },
  { month: '2026-08', count: 116344 },
  { month: '2026-09', count: 100927 },
];

/** 최근 7일 날마다 새로 모인 글 — 줄이 날짜인 답(막대 그림) */
export const TRACK_DAILY: MomentAnalyticsPivotResponse = singleSeries([
  ['2026-09-26', 2604],
  ['2026-09-27', 2759],
  ['2026-09-28', 3460],
  ['2026-09-29', 3650],
  ['2026-09-30', 3876],
  ['2026-10-01', 3782],
  ['2026-10-02', 2469],
]);

/** 추적 세트에 거는 말 — 키워드 셋 · 계정 하나(가상 핸들) */
export const TRACK_SET = { keywords: ['PDRN', '피디알엔', '연어 앰플'], accounts: ['@skinnotes.lab'] };

/**
 * 하루치 Slack 알림 쪽지 — 하루에 새로 모인 글 수 · 도달 합 · 채널 비중은 실제 하루치(반올림),
 * 글 셋은 그날 도달이 큰 해외 글이고 계정은 가상 핸들 · 본문은 해시태그만이다.
 */
export const TRACK_ALERT: AlertPreviewSample = {
  total: 3782,
  stats: [
    { value: '3,782', label: '멘션' },
    { value: '1,060.8만', label: '총 도달' },
    { value: '2,805', label: '평균 도달' },
  ],
  channels: [
    { name: 'instagram', count: 1168, pct: 31 },
    { name: 'naverblog', count: 716, pct: 19 },
    { name: 'tiktok', count: 646, pct: 17 },
  ],
  posts: [
    { channel: 'instagram', account: 'barrier.diaries', date: '2026.10.01', reach: '92.8만', title: '#pdrncream #kbeauty #pharmacybeauty', body: '' },
    { channel: 'instagram', account: 'glow.notes', date: '2026.10.01', reach: '52.8만', title: '#glassskin #darkspot #pdrn', body: '' },
    { channel: 'tiktok', account: 'tinted.tuesday', date: '2026.10.01', reach: '42.1만', title: '#PDRN #PDRNSkinTint #makeup', body: '' },
  ],
};

// ---- 5. 시딩 ---------------------------------------------------------------

/** 시딩 캠페인 하나의 결과 — 실제 캠페인 한 건의 규모(반올림) · 비용은 그 캠페인에 적힌 계정별 예산의 합 */
export const SEEDING_RESULT = {
  accounts: 21,
  uploaded: 11,
  posts: 28,
  views: 445000,
  reactions: 50800,
  costWon: 190540000,
};

/**
 * 계정별 업로드 — 올린 계정 넷(그 글의 그림 · 실제 해외 시딩 글의 반응 수)과 아직 안 올린 계정 둘. 핸들은 가상이다.
 * 올린 글은 컨텐츠 격자 카드로도 편다(`SEEDING_POSTS`).
 */
export const SEEDING_ACCOUNTS: { account: string; channel: 'instagram' | 'tiktok'; post?: { thumb: string; date: string; views: number; likes: number; comments: number } }[] = [
  { account: 'nightly.layers', channel: 'tiktok', post: { thumb: 'seed-post-1.webp', date: '2026-09-22', views: 21708, likes: 1051, comments: 24 } },
  { account: 'neck.and.glow', channel: 'tiktok', post: { thumb: 'seed-post-2.webp', date: '2026-09-23', views: 27149, likes: 3248, comments: 53 } },
  { account: 'shelfie.club', channel: 'instagram', post: { thumb: 'seed-post-3.webp', date: '2026-09-12', views: 46485, likes: 241, comments: 6 } },
  { account: 'cica.corner', channel: 'tiktok', post: { thumb: 'seed-post-4.webp', date: '2026-09-13', views: 16822, likes: 81, comments: 18 } },
  { account: 'matte.morning', channel: 'instagram' },
  { account: 'peachy.pores', channel: 'tiktok' },
];

/** 올린 계정의 글 — 컨텐츠 격자 카드 꼴 */
export const SEEDING_POSTS: MediaCard[] = SEEDING_ACCOUNTS.flatMap((row) =>
  row.post
    ? [
        organic({
          id: `seed-${row.account}`,
          name: row.account,
          channel: row.channel,
          date: row.post.date,
          views: row.post.views,
          likes: row.post.likes,
          comments: row.post.comments,
          title: '#anuapartner #ad',
          thumb: row.post.thumb,
        }),
      ]
    : []
);

// ---- 이하 비공개 ----------------------------------------------------------

/** 발굴 문서 한 건 — 실화면 줄 파생이 읽는 칸만 */
function creator(letter: string, account: string, followers: number, avgView: number, engagementRate: number, postCount: number, previews: number) {
  return {
    doc: {
      id: `home-creator-${letter}`,
      account,
      channel: 'instagram',
      market: 'US',
      followerCount: followers,
      profile: { userName: account, followerCount: followers },
      stats30d: { avgView, viewSum: avgView * postCount, engagementRate, postCount },
    },
    previews: Array.from({ length: previews }, (_, i) => ({
      source: 'instagram',
      imageS3Url: sceneAsset(`creator-${letter.toLowerCase()}-${i + 1}.webp`),
    })),
  };
}
